// Измерение потока документа: обход DOM редактора в порядке документа -> «атомы» (абзацы, раскрываемые в строки;
// неделимые блоки; строки таблиц) в естественных координатах - экранные минус уже стоящие распорки страниц.
// Высоты берутся из настоящей вёрстки браузера (те же шрифты и переносы строк), поэтому разметка совпадает с
// тем, что видно на экране; с Word она совпадает, если CSS листа повторяет параметры writer/builder.py.
import type { EditorView } from '@tiptap/pm/view';
import type { Node as PMNode } from '@tiptap/pm/model';
import { PAGE_W, BODY_TOP } from './geometry';
import type { Atom, ListingInfo, TableInfo } from './types';
import { codeViews, currentCodeBreaks } from './code';

// «Не отрывать от следующего» (keepNext) и «не разрывать абзац» (keepLines) - ровно как флаги DOCX writer (P3):
// keepNext - заголовки, структурные элементы, название таблицы (с первой строкой), головка таблицы (с первой строкой
// данных - sto_lib.keep_table_heads), название листинга (с кодом);
// рисунок с подписью - один неделимый блок (у картинки keepNext - с подписью). keepLines - только заголовки.
// Обычные абзацы (и «…:» перед перечислением/формулой), позиции перечислений, формулы, экспликация «где», строки
// данных таблицы, код листинга - без них: Word делит и переносит их как обычный текст (висячие строки - не меньше 2).

interface Outer { pos: number; el: HTMLElement; side: number }

export interface Measured { atoms: Atom[]; k: number; top: number; left: number; tables: TableInfo[] }

const desc = (el: Element): any => (el as any).pmViewDesc;

/** Выступ последней строки за нижнее поле, допустимый в Word: Word ставит текст строки вверху её высоты, добавку
 *  межстрочного интервала - снизу, и проверяет, помещается ли на странице сама строка текста (одинарная высота
 *  1,15 кегля), а не добавка: у «1,5 строки» 14 пт - 24,15 − 16,1 = 8,05 пт (по PDF из Word). */
const slackCache = new Map<string, number>();
function textSlack(el: Element, k: number): number {
  const key = el.tagName + '|' + (el.className || '') + '|' + (el.parentElement?.tagName || '') + '|' + (el.parentElement?.className || '');
  let v = slackCache.get(key);
  if (v === undefined) {
    const cs = getComputedStyle(el);
    const F = parseFloat(cs.fontSize) || 0;
    const L = parseFloat(cs.lineHeight) || 0;
    v = Math.max(0, L - 1.15 * F);
    if (slackCache.size > 500) slackCache.clear();
    slackCache.set(key, v);
  }
  void k;
  return v;
}

/** Высота абзаца при последнем полном замере (быстрый путь набора: высота не изменилась - разметка та же). */
export const paraHeights = new WeakMap<HTMLElement, number>();

export class Walker {
  atoms: Atom[] = [];
  tables: TableInfo[] = [];
  /** экранная координата - естественная (накопленная высота распорок выше) */
  shift = 0;
  /** накопленная ошибка оценки высоты листингов CodeMirror (на экране есть, в естественных координатах - нет) */
  errCum = 0;
  lastDBottom = BODY_TOP;
  pendingSp: HTMLElement | null = null;
  forceNext = false;
  grp = 0;
  constructor(public view: EditorView, public R: DOMRect, public k: number) {}

  /** px без масштаба от верха основного листа */
  y(v: number) { return (v - this.R.top) / this.k; }
  rect(el: Element) { const r = el.getBoundingClientRect(); return { top: this.y(r.top), bottom: this.y(r.bottom), h: r.height / this.k, r }; }

  push(a: Partial<Atom> & { dTop: number; dBottom: number; kind: Atom['kind'] }, innerSp = 0): Atom {
    if (this.pendingSp) {
      const sp = this.pendingSp;
      this.pendingSp = null;
      const r = this.rect(sp);
      const gapDisp = a.dTop - this.lastDBottom;
      const hSet = parseFloat(sp.getAttribute('data-h') || 'NaN');
      let g = 0;
      if (sp.classList.contains('pg-sp-block')) {
        const mb = r.top - this.lastDBottom;
        const mt = a.dTop - r.bottom;
        a.mbMeas = mb;
        a.mtMeas = mt;
        g = Math.max(mb, mt, 0);
        if (!isNaN(hSet)) a.spC = gapDisp - mb - mt - hSet;
      } else if (!isNaN(hSet)) a.spC = gapDisp - hSet;
      this.shift += gapDisp - g;
    }
    const atom: Atom = {
      top: a.dTop - this.shift, bottom: a.dBottom - this.shift - innerSp, dTop: a.dTop, dBottom: a.dBottom,
      kind: a.kind, brk: a.brk ?? null, pos: a.pos ?? -1, side: a.side ?? 1, outer: a.outer ?? null,
      grp: a.grp ?? -1, li: a.li ?? 0, ln: a.ln ?? 1, keepNext: !!a.keepNext, keepLines: a.keepLines, force: !!a.force || this.forceNext,
      fns: a.fns, expand: a.expand, nodeId: a.nodeId, table: a.table, head: a.head, rowBreakOk: a.rowBreakOk,
      mbMeas: a.mbMeas, mtMeas: a.mtMeas, spC: a.spC, el: a.el ?? null, npos: a.npos, slack: a.slack, errCum: this.errCum,
    };
    if (a.kind !== 'zero') this.forceNext = false;
    this.atoms.push(atom);
    this.shift += innerSp;
    this.lastDBottom = a.dBottom;
    return atom;
  }

  /** Дочерние элементы контейнера (корень редактора, перечисление, приложение, примечание…) по порядку. */
  container(parent: HTMLElement, firstOuter: Outer | null) {
    let first = true;
    for (const child of Array.from(parent.children) as HTMLElement[]) {
      if (child.classList.contains('pg-sp')) { this.pendingSp = child; continue; }
      const d = desc(child);
      if (!d) continue;
      const outer: Outer | null = first && firstOuter ? firstOuter : null;
      if (d.widget) {
        first = false;
        this.widget(child, d, outer);
        continue;
      }
      const node: PMNode | undefined = d.node;
      if (!node) continue;
      first = false;
      const pos: number = d.posBefore;
      this.node(node, pos, child, outer || { pos, el: child, side: 1 });
    }
  }

  /** Список источников (отображение узла / вставка): заголовок и позиции - позиции переносятся на следующую
   *  страницу целиком (распорка - отступ сверху у элемента позиции, вне ProseMirror). */
  sources(head: HTMLElement | null, items: HTMLElement[], all: HTMLElement, base: Partial<Atom>) {
    const r = this.rect(all);
    const hr = head ? this.rect(head) : { top: r.top, bottom: items.length ? this.rect(items[0]).top : r.bottom };
    this.push({ ...base, kind: 'box', dTop: hr.top, dBottom: hr.bottom, keepNext: items.length > 0, keepLines: true, el: head || all } as any);
    items.forEach((it, idx) => {
      const ir = this.rect(it);
      const hSet = parseFloat(it.getAttribute('data-pg-h') || 'NaN');
      let spC: number | undefined;
      if (!isNaN(hSet)) {
        const gap = ir.top - this.lastDBottom;
        this.shift += gap;
        spC = gap - hSet;
      }
      // первая позиция - только вместе с заголовком (распорка перед заголовком)
      this.push({ kind: 'box', dTop: ir.top, dBottom: ir.bottom, brk: idx ? 'dom' : null, pos: -1, keepLines: true, el: it, spC, slack: textSlack(it, this.k) });
    });
  }

  widget(el: HTMLElement, d: any, outer: Outer | null) {
    const r = this.rect(el);
    const pos: number = outer ? outer.pos : d.posBefore;
    const o = outer || { pos, el, side: -2 };
    if (el.classList.contains('sto-ai-widget')) {
      this.push({ kind: 'box', dTop: r.top, dBottom: r.bottom, brk: 'block', pos: o.pos, side: o.side, outer: o.el, force: true, nodeId: 'meta_ai', keepLines: true, el });
    } else if (el.classList.contains('sto-auto-sources')) {
      const items = Array.from(el.querySelectorAll('.sto-source-item')) as HTMLElement[];
      this.sources(el.querySelector('.w-head') as HTMLElement | null, items, el,
        { brk: 'block', pos: o.pos, side: o.side, outer: o.el, force: true, nodeId: 'meta_sources' });
    } else if (r.h > 1) {
      this.push({ kind: 'box', dTop: r.top, dBottom: r.bottom, brk: 'block', pos: o.pos, side: o.side, outer: o.el, el });
    }
  }

  node(n: PMNode, pos: number, el: HTMLElement, outer: Outer) {
    const t = n.type.name;
    const base = { brk: 'block' as const, pos: outer.pos, side: outer.side, outer: outer.el, el };
    switch (t) {
      case 'paragraph':
        this.para(n, pos, el, outer);
        return;
      case 'heading': {
        const r = this.rect(el);
        this.push({ ...base, kind: 'box', dTop: r.top, dBottom: r.bottom, keepNext: true, keepLines: true, force: n.attrs.level === 1, nodeId: n.attrs.id, slack: textSlack(el, this.k) });
        return;
      }
      case 'structHeading': {
        const items = Array.from(el.querySelectorAll('.sto-source-item')) as HTMLElement[];
        if (n.attrs.kind === 'sources' && items.length) {
          this.sources(el.querySelector('.sto-struct-title') as HTMLElement | null, items, el, { ...base, force: true, nodeId: n.attrs.id });
          return;
        }
        const r = this.rect(el);
        this.push({ ...base, kind: 'box', dTop: r.top, dBottom: r.bottom, keepNext: true, keepLines: true, force: true, nodeId: n.attrs.id });
        return;
      }
      case 'appendixTitle': {
        const r = this.rect(el);
        this.push({ ...base, kind: 'box', dTop: r.top, dBottom: r.bottom, keepNext: true, keepLines: true });
        return;
      }
      case 'appendix': {
        this.forceNext = true;
        const start = this.atoms.length;
        this.container(el, outer);
        if (this.atoms[start]) { this.atoms[start].force = true; this.atoms[start].nodeId = n.attrs.id; }
        return;
      }
      case 'stoList':
      case 'stoListItem':
      case 'note':
      case 'example':
        this.container(el, outer);
        return;
      case 'stoTable':
        this.table(n, el, outer);
        return;
      case 'listing':
        this.listing(n, el, outer);
        return;
      case 'pageBreak': {
        const r = this.rect(el);
        this.push({ kind: 'zero', dTop: r.top, dBottom: r.top, brk: null, el });
        this.forceNext = true;
        return;
      }
      case 'formula':
        this.formula(el, base);
        return;
      default: {
        const r = this.rect(el);
        if (r.h <= 0 && t !== 'figure') return;
        this.push({ ...base, kind: 'box', dTop: r.top, dBottom: r.bottom, keepLines: true });
      }
    }
  }

  /** Формула: строка формулы - неделимый блок; строки экспликации «где …» - отдельные абзацы DOCX (без «не
   *  отрывать»): разрыв страницы между ними - отступом сверху у строки (как у позиций списка источников). */
  formula(el: HTMLElement, base: Partial<Atom>) {
    const r = this.rect(el);
    const rows = Array.from(el.querySelectorAll('.sto-where-row')) as HTMLElement[];
    // низ строки формулы - низ .mx-box (у .sto-where верх не годится: отступ-распорка первой строки «где»
    // схлопывается с ним и сдвигает его вниз)
    const box = rows.length ? el.querySelector('.mx-box') as HTMLElement | null : null;
    if (!box) {
      this.push({ ...base, kind: 'box', dTop: r.top, dBottom: r.bottom, keepLines: true } as any);
      return;
    }
    this.push({ ...base, kind: 'box', dTop: r.top, dBottom: this.rect(box).bottom, keepLines: true } as any);
    rows.forEach((it, idx) => {
      const ir = this.rect(it);
      const hSet = parseFloat(it.getAttribute('data-pg-h') || 'NaN');
      let spC: number | undefined;
      if (!isNaN(hSet)) {
        const gap = ir.top - this.lastDBottom;
        this.shift += gap;
        spC = gap - hSet;
      }
      this.push({ kind: 'box', dTop: ir.top, dBottom: idx === rows.length - 1 ? Math.max(ir.bottom, r.bottom) : ir.bottom, brk: 'dom',
        pos: -1, keepLines: true, el: it, spC, slack: textSlack(it, this.k) });
    });
  }

  para(n: PMNode, pos: number, el: HTMLElement, outer: Outer) {
    const r = this.rect(el);
    paraHeights.set(el, r.h);
    const isp = el.querySelector(':scope > .pg-sp-inline') as HTMLElement | null;
    const ispH = isp ? this.rect(isp).h : 0;
    const keepNext = false;                        // абзацы и позиции перечислений - без «не отрывать», как в DOCX
    const grp = ++this.grp;
    const fnl: { id: string; pos: number }[] = [];
    n.descendants((c, p) => { if (c.type.name === 'footnote' && c.attrs.id) fnl.push({ id: c.attrs.id, pos: pos + 1 + p }); return true; });
    const w = this;
    const atom = this.push({ kind: 'para', dTop: r.top, dBottom: r.bottom, brk: 'block', pos: outer.pos, side: outer.side, outer: outer.el,
      keepNext, grp, fns: fnl.length ? fnl.map((f) => f.id) : undefined, el, slack: textSlack(el, this.k) }, ispH);
    const shift0 = atom.dTop - atom.top;          // распорка перед абзацем уже учтена
    atom.expand = () => w.lines(el, isp, pos, n, atom, shift0, fnl);
  }

  /** Строки абзаца: прямоугольники текста, сгруппированные по строкам; границы строк - по межстрочному интервалу. */
  lines(el: HTMLElement, isp: HTMLElement | null, pos: number, n: PMNode, para: Atom, shift0: number, fnl: { id: string; pos: number }[]): Atom[] {
    const k = this.k;
    const range = document.createRange();
    range.selectNodeContents(el);
    const spr = isp ? isp.getBoundingClientRect() : null;
    const rects = Array.from(range.getClientRects()).filter((q) => {
      if (q.width <= 0 && q.height <= 0) return false;
      if (spr && q.top >= spr.top - 0.5 && q.bottom <= spr.bottom + 0.5 && q.height > spr.height * 0.6) return false;
      return true;
    }).sort((a, b) => a.top - b.top || a.left - b.left);
    const L = (parseFloat(getComputedStyle(el).lineHeight) || 24) * k;   // экранные px
    const lines: { top: number; bottom: number; left: number; cy: number }[] = [];
    for (const q of rects) {
      const cur = lines[lines.length - 1];
      const cy = (q.top + q.bottom) / 2;
      if (cur && cy < cur.bottom - 0.5 && q.top < cur.bottom - Math.min(2 * k, (cur.bottom - cur.top) / 3)) {
        cur.top = Math.min(cur.top, q.top);
        cur.bottom = Math.max(cur.bottom, q.bottom);
        cur.left = Math.min(cur.left, q.left);
      } else lines.push({ top: q.top, bottom: q.bottom, left: q.left, cy });
    }
    const er = el.getBoundingClientRect();
    if (!lines.length) return [{ ...para, kind: 'line', li: 0, ln: 1, expand: undefined }];
    // строковые коробки: центр содержимого ± интервал/2; первая - от верха абзаца, последняя - до низа
    const boxes = lines.map((l) => {
      const c = (l.top + l.bottom) / 2;
      return { top: Math.min(c - L / 2, l.top), bottom: Math.max(c + L / 2, l.bottom), left: l.left, cy: c };
    });
    boxes[0].top = er.top;
    boxes[boxes.length - 1].bottom = er.bottom;
    const spBetween = (i: number) => !!spr && spr.top >= boxes[i - 1].cy && spr.bottom <= boxes[i].cy + 1;
    for (let i = 1; i < boxes.length; i++) {
      if (spBetween(i)) {
        // строки вплотную к распорке: её края - точные границы строк (текст строки со сноской/индексом
        // стоит в своей строке ниже середины, и «середина ± интервал/2» ошибается на несколько px)
        boxes[i - 1].bottom = spr!.top;
        boxes[i].top = spr!.bottom;
        continue;
      }
      const m = (boxes[i].top + boxes[i - 1].bottom) / 2;
      boxes[i - 1].bottom = m;
      boxes[i].top = m;
    }
    const view = this.view;
    const fnAt = fnl.map((f) => {
      const dom = view.nodeDOM(f.pos) as HTMLElement | null;
      const q = dom && dom.nodeType === 1 ? dom.getBoundingClientRect() : null;
      return { id: f.id, cy: q ? (q.top + q.bottom) / 2 : NaN };
    });
    const out: Atom[] = [];
    let acc = shift0;
    const endPos = pos + n.nodeSize - 1;
    for (let i = 0; i < boxes.length; i++) {
      const b = boxes[i];
      let spC: number | undefined;
      if (i > 0 && spBetween(i)) {
        const gap = (b.top - boxes[i - 1].bottom) / k;
        acc += gap;
        const hSet = parseFloat(isp?.getAttribute('data-h') || 'NaN');
        if (!isNaN(hSet)) spC = gap - hSet;
      }
      const dTop = this.y(b.top);
      const dBottom = this.y(b.bottom);
      const fns = fnAt.filter((f) => (f.cy >= b.top && f.cy < b.bottom) || (isNaN(f.cy) && i === 0)).map((f) => f.id);
      const a: Atom = {
        top: dTop - acc, bottom: dBottom - acc, dTop, dBottom, kind: 'line',
        brk: i === 0 ? para.brk : 'inline', pos: i === 0 ? para.pos : -1, side: i === 0 ? para.side : -1, outer: i === 0 ? para.outer : null,
        grp: para.grp, li: i, ln: boxes.length, keepNext: i === boxes.length - 1 ? para.keepNext : false,
        force: i === 0 ? para.force : false, fns: fns.length ? fns : undefined, nodeId: i === 0 ? para.nodeId : undefined,
        mbMeas: i === 0 ? para.mbMeas : undefined, mtMeas: i === 0 ? para.mtMeas : undefined, el,
        spC: i === 0 ? para.spC : spC, slack: para.slack, errCum: para.errCum,
      };
      if (i > 0) {
        const x = b.left + 1;
        const y = b.top;
        Object.defineProperty(a, 'pos', {
          configurable: true, enumerable: true,
          get() {
            const v = linePos(view, x, y, pos + 1, endPos);
            Object.defineProperty(a, 'pos', { value: v, writable: true, enumerable: true });
            return v;
          },
        });
      }
      out.push(a);
    }
    return out;
  }

  table(n: PMNode, el: HTMLElement, outer: Outer) {
    let num = '';
    const cap = el.querySelector(':scope > .sto-caption') as HTMLElement | null;
    const lab = cap?.getAttribute('data-label') || '';
    const m = /Таблица\s*([0-9A-ZА-Я][0-9.A-ZА-Я]*)/.exec(lab.replace(/ /g, ' '));
    if (m) num = m[1].replace(/\.$/, '');
    const info: TableInfo = { num, headH: 0, headRows: 0, table: null, cols: 1, firstAtom: null };
    const start = this.atoms.length;
    this.tables.push(info);
    let first = true;
    for (const child of Array.from(el.children) as HTMLElement[]) {
      if (child.classList.contains('pg-sp')) { this.pendingSp = child; continue; }
      const d = desc(child);
      if (!d || !d.node) continue;
      const o: Outer | null = first ? outer : null;
      const cpos: number = d.posBefore;
      const tn = d.node.type.name;
      if (tn === 'caption') {
        const r = this.rect(child);
        // название таблицы: «не отрывать» (с первой строкой таблицы); «не разрывать» у него нет (блок и так неделим)
        this.push({ kind: 'box', dTop: r.top, dBottom: r.bottom, brk: 'block', pos: (o || { pos: cpos }).pos, side: o ? o.side : 1,
          outer: o ? o.el : child, keepNext: true, table: info, el: child });
      } else if (tn === 'table') {
        this.rows(d.node, cpos, child, o, info);
      } else {
        const r = this.rect(child);
        this.push({ kind: 'box', dTop: r.top, dBottom: r.bottom, brk: 'block', pos: (o || { pos: cpos }).pos, side: o ? o.side : 1,
          outer: o ? o.el : child, keepLines: true, table: info, el: child });
      }
      first = false;
    }
    info.firstAtom = this.atoms[start] || null;
  }

  rows(tnode: PMNode, tpos: number, wrap: HTMLElement, outer: Outer | null, info: TableInfo) {
    const table = wrap.tagName === 'TABLE' ? wrap as HTMLTableElement : wrap.querySelector('table');
    const body = table?.tBodies[0] || table;
    if (!table || !body) return;
    info.table = table as HTMLTableElement;
    let spanUntil = -1;
    let r = 0;
    let headDone = false;
    let cols = 1;
    for (const tr of Array.from(body.children) as HTMLElement[]) {
      if (tr.classList.contains('pg-sp')) { this.pendingSp = tr; continue; }
      const d = desc(tr);
      if (!d || !d.node) continue;
      const cells = Array.from((tr as HTMLTableRowElement).cells || []);
      const head = !headDone && cells.length > 0 && cells.every((c) => c.tagName === 'TH');
      if (!head) headDone = true;
      let c = 0;
      for (const cell of cells) c += cell.colSpan || 1;
      cols = Math.max(cols, c);
      // начать страницу можно не с объединённой по вертикали строки и не со второй строки головки (головка - вместе)
      const ok = r > spanUntil && !(head && r > 0);
      for (const cell of cells) if ((cell.rowSpan || 1) > 1) spanUntil = Math.max(spanUntil, r + cell.rowSpan - 1);
      const rr = this.rect(tr);
      const firstRow = r === 0 && outer;
      this.push({ kind: 'row', dTop: rr.top, dBottom: rr.bottom, brk: firstRow ? 'block' : 'row', pos: firstRow ? outer!.pos : d.posBefore,
        side: firstRow ? outer!.side : 1, outer: firstRow ? outer!.el : null, keepNext: head, table: info, head, rowBreakOk: ok, el: tr, npos: d.posBefore });
      if (head) { info.headRows++; info.headH += rr.h; }
      r++;
    }
    info.cols = cols;
    // головка - не меньше одной строки (writer/builder.py emit_table: hdr = max(1, …))
    if (!info.headRows && r > 1) {
      const fr = this.atoms.find((a) => a.table === info && a.kind === 'row');
      if (fr) { fr.head = true; info.headRows = 1; info.headH = fr.dBottom - fr.dTop; }
    }
  }

  listing(n: PMNode, el: HTMLElement, outer: Outer) {
    const cap = el.querySelector('.sto-listing-cap') as HTMLElement | null;
    const code = el.querySelector('.sto-listing-code') as HTMLElement | null;
    const r = this.rect(el);
    if (!cap || !code) {
      this.push({ kind: 'box', dTop: r.top, dBottom: r.bottom, brk: 'block', pos: outer.pos, side: outer.side, outer: outer.el, keepLines: true, el });
      return;
    }
    const rc = this.rect(cap);
    const rk = this.rect(code);
    // название листинга: «не отрывать» (с первой строкой кода)
    this.push({ kind: 'box', dTop: rc.top, dBottom: rc.bottom, brk: 'block', pos: outer.pos, side: outer.side, outer: outer.el, keepNext: true, el: cap });
    // код - абзац DOCX без «не разрывать»: Word делит его по строкам (не меньше 2 строк внизу и вверху), а writer
    // по этим границам страниц делит его на части с «Продолжение/Окончание листинга N» (engine.ts)
    const lines = String(n.attrs.code || '').replace(/\n+$/, '').split('\n').length;
    const id: string = n.attrs.id;
    const cm = id ? codeViews.get(id) : undefined;
    if (!cm || !cm.dom.isConnected) {
      this.push({ kind: 'box', dTop: rk.top, dBottom: Math.max(rk.bottom, r.bottom), brk: null, keepLines: true, el: code });
      return;
    }
    // Высоты строк кода - своим замером (codeLineHeights), а не из CodeMirror: CodeMirror рисует только строки у
    // окна, высоты остальных он ОЦЕНИВАЕТ (13-19 px вместо 15,1) - разметка по ним «качалась» бесконечно (каждый
    // кадр - новая высота распорки в листинге), ставила разрыв не на ту строку, а всё, что ниже листинга, получало
    // ошибку оценки. Высота рамки на экране, пока CodeMirror её не уточнил, отличается от расчётной - эта разница
    // учитывается как распорка (в естественных координатах её нет).
    const hs = codeLineHeights(cm, this.k);
    const nl = Math.max(1, Math.min(cm.state.doc.lines, lines, hs.length));
    const cr = cm.contentDOM.getBoundingClientRect();
    const ccs = getComputedStyle(cm.contentDOM);
    const kcs = getComputedStyle(code);
    const padTop = parseFloat(ccs.paddingTop) || 0;
    const tailPad = (parseFloat(ccs.paddingBottom) || 0) + (parseFloat(kcs.paddingBottom) || 0) + (parseFloat(kcs.borderBottomWidth) || 0);
    const brs = currentCodeBreaks(id);
    let acc = this.y(cr.top) + padTop;
    const tops: number[] = [];
    for (let i = 1; i <= nl; i++) {
      if (i > 1 && brs.has(i)) acc += brs.get(i) || 0;             // распорка-виджет перед строкой i
      tops.push(acc);
      acc += hs[i - 1] || 0;
    }
    const frameBottom = acc + tailPad;                                // низ рамки по расчёту (экранные коорд.)
    const err = rk.bottom - frameBottom;                              // ошибка оценки CodeMirror (0, если отрисован)
    const grp = ++this.grp;
    // номер - из подписи («Листинг 2. » ставит плагин нумерации); рамка части листинга на странице продолжения - как у
    // всего листинга (writer делит код на абзацы того же стиля): сверху линия и поле до первой строки; снизу поле и
    // линия - только у последней части (у части перед разрывом страницы рамка открыта снизу, writer/listingsplit.py)
    const lab = (cap.querySelector('.sto-caption') || cap).getAttribute('data-label') || '';
    const mn = /Листинг\s*([0-9A-ZА-Я][0-9.A-ZА-Я]*)/.exec(lab.replace(/\u00a0/g, ' '));
    const info: ListingInfo = {
      id, num: mn ? mn[1].replace(/\.$/, '') : '', padTop: Math.max(0, tops[0] - rk.top), padBottom: tailPad,
      textDx: (r.r.left - cr.left) / this.k, frameDx: (rk.r.left - cr.left) / this.k, frameW: rk.r.width / this.k,
    };
    for (let i = 1; i <= nl; i++) {
      const tDisp = tops[i - 1];
      const bDisp = i < nl ? tops[i] - (brs.get(i + 1) || 0) : frameBottom;
      const dTop = i === 1 ? rk.top : tDisp;
      const dBottom = bDisp;
      let spC: number | undefined;
      if (i > 1 && brs.has(i)) {
        const gap = dTop - this.lastDBottom;
        this.shift += gap;
        spC = gap - (brs.get(i) || 0);
      }
      this.push({ kind: 'line', dTop, dBottom, brk: i === 1 ? null : 'code', grp, li: i - 1, ln: nl, keepLines: false, el: code, spC });
      const at = this.atoms[this.atoms.length - 1];
      at.codeId = id;
      at.codeLine = i;
      at.listing = info;
    }
    this.shift += err;
    this.errCum += err;
    this.lastDBottom += err;
  }

  finish() {
    // последняя позиция перечня без «не отрывать»: последний атом документа ни к чему не привязан
    const last = this.atoms[this.atoms.length - 1];
    if (last) last.keepNext = false;
  }
}

/** Высоты строк кода листинга (px без масштаба, с переносом длинных строк) - скрытым элементом с тем же шрифтом и
 *  шириной, что у строк CodeMirror. Кэш - по тексту и ширине. */
const codeHCache = new WeakMap<object, { key: string; hs: number[] }>();
let codeMeasurer: HTMLDivElement | null = null;
export function codeLineHeights(cm: any, k: number): number[] {
  const content: HTMLElement = cm.contentDOM;
  const text: string = cm.state.doc.toString();
  const width = content.getBoundingClientRect().width / (k || 1);
  const cs = getComputedStyle(content);
  const key = width.toFixed(2) + '|' + cs.fontSize + '|' + cs.lineHeight + '|' + cs.fontFamily + '|' + text;
  const hit = codeHCache.get(cm);
  if (hit && hit.key === key) return hit.hs;
  if (!codeMeasurer) {
    codeMeasurer = document.createElement('div');
    codeMeasurer.setAttribute('aria-hidden', 'true');
    codeMeasurer.style.cssText = 'position:absolute;left:-99999px;top:0;visibility:hidden;pointer-events:none;contain:layout style;';
    document.body.appendChild(codeMeasurer);
  }
  const m = codeMeasurer;
  m.style.width = width + 'px';
  m.style.fontFamily = cs.fontFamily;
  m.style.fontSize = cs.fontSize;
  m.style.lineHeight = cs.lineHeight;
  m.style.letterSpacing = cs.letterSpacing;
  m.style.fontKerning = cs.fontKerning;
  m.style.fontVariantLigatures = cs.fontVariantLigatures;
  (m.style as any).tabSize = (cs as any).tabSize || '4';
  m.style.whiteSpace = 'pre-wrap';
  (m.style as any).overflowWrap = 'anywhere';
  m.style.wordBreak = 'normal';
  m.textContent = '';
  const lines = text.split('\n');
  const frag = document.createDocumentFragment();
  for (const ln of lines) {
    const d = document.createElement('div');
    if (ln) d.textContent = ln; else d.appendChild(document.createElement('br'));
    frag.appendChild(d);
  }
  m.appendChild(frag);
  const hs = Array.from(m.children).map((d) => (d as HTMLElement).getBoundingClientRect().height);
  m.textContent = '';
  codeHCache.set(cm, { key, hs });
  return hs;
}

/** Позиция начала строки: первая позиция абзаца, символ после которой стоит ниже границы строк (двоичный поиск
 *  по coordsAtPos - устойчив к распоркам и к кавычкам/тире в начале строки). y - граница строк (экранная, клиентская). */
export function linePos(view: EditorView, _x: number, y: number, from: number, to: number): number {
  const below = (p: number) => {
    try { const c = view.coordsAtPos(p, 1); return (c.top + c.bottom) / 2 > y; } catch { return false; }
  };
  let lo = from;
  let hi = to;
  if (!below(hi)) return -1;
  if (below(lo)) return -1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (below(mid)) hi = mid; else lo = mid;
  }
  return hi;
}

export function measure(view: EditorView, host: HTMLElement): Measured | null {
  const R = host.getBoundingClientRect();
  if (!R.width || !view.dom.isConnected) return null;
  const k = R.width / PAGE_W;
  const w = new Walker(view, R, k);
  w.container(view.dom as HTMLElement, null);
  w.finish();
  return { atoms: w.atoms, k, top: R.top, left: R.left, tables: w.tables };
}
